import React, { type ReactNode, useContext, useMemo } from 'react';

import {
	RelayEnvironmentProvider as DefaultProvider,
	type IEnvironmentProvider,
	ReactRelayContext,
	useRelayEnvironment,
} from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isAtlassianRelayEnvironment } from '@atlassian/relay-environment/createRelayEnvironment';

import { __INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__ } from './environment';

export function AtlassianRelayEnvironmentProvider({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element {
	const existingRelayContext = useContext(ReactRelayContext);

	// If a compatible relay environment (one created via `createRelayEnvironment`) is already present
	// in the tree, reuse it instead of shadowing it with our own internal environment.
	if (
		existingRelayContext != null &&
		isAtlassianRelayEnvironment(existingRelayContext.environment) &&
		fg('relay_environment_-_reuse_product_env_when_exist')
	) {
		return <>{children}</>;
	}

	// Otherwise, fall back to providing the default internal relay environment.
	return (
		<DefaultProvider environment={__INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__}>
			{children}
		</DefaultProvider>
	);
}

export const RelayEnvironmentProvider: typeof AtlassianRelayEnvironmentProvider =
	AtlassianRelayEnvironmentProvider;

export const ConditionalRelayEnvironmentProvider = ({
	children,
}: {
	children: ReactNode;
}):
	| string
	| number
	| boolean
	| Iterable<React.ReactNode>
	| React.JSX.Element
	| null
	| undefined => {
	const isRelayPresent = useContext(ReactRelayContext);

	if (isRelayPresent) {
		return children;
	}

	return <AtlassianRelayEnvironmentProvider>{children}</AtlassianRelayEnvironmentProvider>;
};

export function useRelayEnvironmentProvider(): IEnvironmentProvider<Record<string, never>> {
	const environment = useRelayEnvironment();

	return useMemo(
		() => ({
			getEnvironment() {
				return environment;
			},
		}),
		[environment],
	);
}
